import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLocale } from '@/contexts/LocaleContext';
import { getNotificationPreferences, updateNotificationPreferences } from '@/lib/api';
import { 
  ArrowLeft, 
  Bell, 
  Loader2,
  Save,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const NotificationPreferences = () => {
  const navigate = useNavigate();
  const { isUSA } = useLocale();
  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const data = await getNotificationPreferences();
        setPreferences(data.preferences || []);
      } catch (error) {
        console.error('Error fetching preferences:', error);
        toast.error(isUSA ? 'Error loading preferences' : 'Error al cargar preferencias');
      } finally {
        setLoading(false);
      }
    };
    fetchPreferences();
  }, [isUSA]);

  const handleToggle = (type) => {
    setPreferences(prev => 
      prev.map(p => 
        p.type === type ? { ...p, enabled: !p.enabled } : p
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const prefsObject = preferences.reduce((acc, p) => {
        acc[p.type] = p.enabled;
        return acc;
      }, {});
      
      await updateNotificationPreferences(prefsObject);
      toast.success(isUSA ? 'Preferences saved' : 'Preferencias guardadas', {
        icon: <CheckCircle className="w-4 h-4 text-emerald-600" />
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error(isUSA ? 'Error saving preferences' : 'Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    setPreferences(prev => prev.map(p => ({ ...p, enabled: true })));
  };

  const handleDisableAll = () => {
    setPreferences(prev => prev.map(p => ({ ...p, enabled: false })));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Group preferences by category
  const quotePrefs = preferences.filter(p => p.type.includes('quote'));
  const orderPrefs = preferences.filter(p => p.type.includes('order') || p.type.includes('sample'));
  const messagePrefs = preferences.filter(p => p.type.includes('message'));
  const accessPrefs = preferences.filter(p => p.type.includes('contact') || p.type.includes('catalog'));
  const otherPrefs = preferences.filter(p => 
    !p.type.includes('quote') && 
    !p.type.includes('order') && 
    !p.type.includes('sample') && 
    !p.type.includes('message') && 
    !p.type.includes('contact') && 
    !p.type.includes('catalog')
  );

  const PreferenceGroup = ({ title, items }) => {
    if (items.length === 0) return null;
    
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">{title}</h3>
        <div className="space-y-2">
          {items.map((pref) => (
            <div 
              key={pref.type} 
              className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <Label htmlFor={pref.type} className="cursor-pointer flex-1">
                {isUSA ? pref.label_en : pref.label_es}
              </Label>
              <Switch
                id={pref.type}
                checked={pref.enabled}
                onCheckedChange={() => handleToggle(pref.type)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="notification-preferences-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isUSA ? 'Notification Preferences' : 'Preferencias de Notificaciones'}
          </h1>
          <p className="text-sm text-slate-500">
            {isUSA 
              ? 'Choose which notifications you want to receive' 
              : 'Elige qué notificaciones deseas recibir'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-emerald-600" />
            {isUSA ? 'Real-time Notifications' : 'Notificaciones en Tiempo Real'}
          </CardTitle>
          <CardDescription>
            {isUSA 
              ? 'You will receive instant notifications with sound for the types you enable. Email notifications will also follow these preferences.'
              : 'Recibirás notificaciones instantáneas con sonido para los tipos que habilites. Las notificaciones por email también seguirán estas preferencias.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick actions */}
          <div className="flex gap-2 pb-4 border-b">
            <Button variant="outline" size="sm" onClick={handleEnableAll}>
              {isUSA ? 'Enable all' : 'Habilitar todas'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDisableAll}>
              {isUSA ? 'Disable all' : 'Deshabilitar todas'}
            </Button>
          </div>

          <PreferenceGroup 
            title={isUSA ? 'Quotes' : 'Cotizaciones'} 
            items={quotePrefs} 
          />
          
          <PreferenceGroup 
            title={isUSA ? 'Orders & Samples' : 'Pedidos y Muestras'} 
            items={orderPrefs} 
          />
          
          <PreferenceGroup 
            title={isUSA ? 'Messages' : 'Mensajes'} 
            items={messagePrefs} 
          />
          
          <PreferenceGroup 
            title={isUSA ? 'Access Requests' : 'Solicitudes de Acceso'} 
            items={accessPrefs} 
          />
          
          <PreferenceGroup 
            title={isUSA ? 'Other' : 'Otras'} 
            items={otherPrefs} 
          />

          {/* Save button */}
          <div className="pt-4 border-t">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isUSA ? 'Save Preferences' : 'Guardar Preferencias'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPreferences;
